/**
 * The SHOW NIGHT day boundary — the one place the 6am rule lives on the client.
 *
 * A show's times are stored as wall-clock with no date (`starts_at`, `doors_time`, `show_end_time`). That is
 * the right shape — a run of show is written in venue-local wall time and never moves when a timezone or a
 * date does — but it leaves one question open: **which day does `00:30` belong to?**
 *
 * The answer is the show NIGHT, not the calendar day. A 00:30 headliner set and a 01:30 load-out are the tail
 * of the evening that started at 16:00, not events twenty-three hours earlier. So on a monotonic minute axis
 * anchored at 06:00, anything before the boundary sits +1440 — after the evening's rows, in the order a
 * person on the ground experiences them.
 *
 * **Why 06:00 and not midnight.** Nothing about a show happens between 4am and 6am, so the boundary can sit
 * anywhere in that dead zone; 6am is the legacy app's choice and it survives a late curfew (a 03:00 load-out
 * still reads as tonight) without ever mis-claiming a genuine early-morning festival call time.
 *
 * This rule shipped first inside the mobile day-of-show clock and existed nowhere else, which is how a 00:30
 * set came to resolve to 00:30 on the show DATE — roughly 24 hours early — everywhere else that touched it.
 * It is hoisted here so the editor, the clock and the ordering all read the same definition.
 *
 * **The other two copies.** The rule also has to exist in C# (`ShowNight`, for the server-side ordering) and
 * in SQL (`artist.show_night_instant`, for the reminder sweep, which cannot call either). Three languages,
 * one rule: if you change the boundary, change all three — `showNight.test.ts` and the backend's
 * `ShowNightTests` both pin the constant so a lone edit fails loudly.
 */

/** Minutes since midnight at which a new show night begins. Times below this belong to the previous night. */
export const SHOW_NIGHT_BOUNDARY_MIN = 6 * 60;

const DAY_MIN = 24 * 60;

/**
 * Place a wall-clock minute on the show night's monotonic axis: `06:00`–`23:59` stay put, `00:00`–`05:59`
 * move a full day later. Comparing or sorting two times is only meaningful once BOTH have been through here.
 */
export function showNightMinute(minutesSinceMidnight: number): number {
  return minutesSinceMidnight < SHOW_NIGHT_BOUNDARY_MIN ? minutesSinceMidnight + DAY_MIN : minutesSinceMidnight;
}

/** `HH:mm[:ss]` (a `time`-format wall clock, no date) → minutes since midnight, or null if unparseable. */
export function parseWallClock(t: string | null | undefined): number | null {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return null;
  return h * 60 + min;
}

/**
 * The show-night axis position of a stored wall clock, or null when it does not parse. The composition
 * callers almost always want — parse, then place.
 */
export function showNightPosition(t: string | null | undefined): number | null {
  const raw = parseWallClock(t);
  return raw == null ? null : showNightMinute(raw);
}

/**
 * Does this wall clock fall in the post-midnight tail? Drives the editor's `(next day)` hint — the reason a
 * `12:30 am` show end is not a mistake, shown rather than silently assumed.
 */
export function isNextDay(t: string | null | undefined): boolean {
  const raw = parseWallClock(t);
  return raw != null && raw < SHOW_NIGHT_BOUNDARY_MIN;
}

// ---- typing a time, rather than hunting for it in a list -------------------------------------------------

/**
 * Parse a time the way a tour manager types one: `2pm`, `3:15`, `8pm`, `9:30 pm`, `19:00`.
 *
 * The run of show is entered as free text in the reference — only DURATIONS are dropdowns — and this is why
 * that works. A 97-option clock list is not more precise, it is just slower: the same seven values, hunted
 * for instead of typed.
 *
 * **The show-time heuristic.** A bare hour below 8 is read as PM (`3:15` → 15:15). Shows happen in the
 * evening, so `3:15` on a day sheet is never quarter past three in the morning — and the one case that
 * genuinely is early (a post-midnight load-out) is written `1am`, which carries its own meridiem. Legacy
 * shipped this rule; it is reproduced exactly rather than "fixed", because the alternative silently reads a
 * typed `7` as breakfast.
 *
 * Returns minutes since midnight, or null when the input cannot be read at all.
 */
export function parseLooseTime(input: string | null | undefined): number | null {
  if (!input) return null;
  const t = String(input).trim().toLowerCase().replace(/\s+/g, "");
  if (!t) return null;
  const m = /^(\d{1,2})(?::(\d{2}))?(am|pm)?$/.exec(t);
  if (!m) return null;
  let h = Number(m[1]);
  const mins = m[2] ? Number(m[2]) : 0;
  if (h > 23 || mins > 59) return null;
  if (m[3] === "pm" && h < 12) h += 12;
  else if (m[3] === "am" && h === 12) h = 0;
  else if (!m[3] && h < 8) h += 12;   // the show-time heuristic
  return h * 60 + mins;
}

/** Minutes since midnight → the compact form a day sheet reads in: `7pm`, `7:15pm`, `12:30am`. */
export function formatShowTime(minutes: number | null): string {
  if (minutes == null || !Number.isFinite(minutes)) return "";
  const n = ((minutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(n / 60);
  const m = n % 60;
  const suffix = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, "0")}${suffix}`;
}

/** Minutes since midnight → the stored `HH:mm` wall clock the API takes. */
export function toWallClock(minutes: number | null): string | null {
  if (minutes == null || !Number.isFinite(minutes)) return null;
  const n = ((minutes % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
}
